/**
 * The browser autoplay bot's client-side run state — drives the shipped
 * `autoplayActivation`/`autoplaySide` (`@lancer-vtt/ai`) over a `BrowserMatchHandle`
 * (`./browserMatchHandle.ts`) UNCHANGED; this file never re-implements or re-derives the policy,
 * only the UI-facing run/halt/resume bookkeeping around it.
 *
 * **One bot run at a time per client.** A single `useAutoplayBot()` instance is meant to be lifted
 * to ONE place (`CombatApp.tsx`) and threaded down to every per-unit panel, so clicking "play" on
 * one NPC disables every OTHER panel's play buttons too, not just its own. `busy` is `true` for
 * every phase except `"idle"` — a halted or failed run still occupies the one slot until the GM
 * resumes or dismisses it.
 *
 * **Halt-for-humans is legible and resumable, on purpose.** When `autoplayActivation`/`autoplaySide`
 * halts on a prompt owned by a DIFFERENT human, the underlying `RemoteEncounterController.prompt`
 * this bot rides is the SAME field `CombatApp.tsx`'s existing `PromptModal`/COMMS-card machinery
 * already renders for that human (and, per `suppressPromptModalForGmBystander`, backstops for a GM
 * bystander) — so answering it needs no new UI here. The bot's own run state does need one: a
 * silent halt with no affordance is a defect, so `describeActivationHalt`/`describeSideHalt` turn
 * every non-`"completed"` outcome into a human-readable reason. `resume()` just re-invokes the SAME
 * operation from the CURRENT authoritative state — `autoplayActivation`/`autoplaySide` are
 * resumable by construction (a fresh call re-reads `handle.getState()` and picks up wherever the
 * turn/activation genuinely stands), so "resume" is never anything more than "call it again."
 *
 * **A `rejection` with `category: "timeout"` is the one resumable rejection** — `BrowserMatchHandle`
 * surfaces a missed correlation signal from its bounded wait as exactly this shape. It is NOT the
 * genuine rules-rejection `autoplayActivation`'s own doc calls "should not happen... a real
 * occurrence is a genuine bug worth surfacing loudly, never silently retried" — that class stays
 * non-resumable here, dismiss-only, so a real bug is never quietly re-driven into the same wall.
 */
import { useCallback, useRef, useState } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import type { Side } from "@lancer-vtt/domain";
import { autoplayActivation, autoplaySide, type AutoplayOutcome, type AutoplaySideOutcome } from "@lancer-vtt/ai";
import { BrowserMatchHandle, browserPromptPolicy } from "./browserMatchHandle";
import { errorMessage } from "@lancer-vtt/shared";

/** One non-`"completed"` outcome's UI-facing shape: what to tell the GM, and whether "resume" means anything for it. */
export type HaltInfo = { reason: string; resumable: boolean };

/** Resolve a combatant id to its display name off the state a halt's own last step carries — never a hand-rolled lookup against a possibly-stale outer `state`. */
function nameIn(state: AutoplayOutcome["steps"][number]["observation"]["state"], id: string): string {
  return state.combatants[id]?.name ?? id;
}

/**
 * Turn one `autoplayActivation` outcome into a GM-facing halt reason, or `null` for a clean
 * `"completed"` (nothing to show — the existing UI already reflects the advanced state).
 */
export function describeActivationHalt(outcome: AutoplayOutcome): HaltInfo | null {
  switch (outcome.kind) {
    case "completed":
      return null;
    case "not_acting":
      return { reason: "not this unit's turn — nothing to play", resumable: false };
    case "prompted": {
      // `outcome.steps` always includes the halting submission (`AutoplayOutcome`'s own doc), so
      // its last step's observation state is the state as of THIS prompt — never a stale outer read.
      const last = outcome.steps[outcome.steps.length - 1];
      const who = last ? nameIn(last.observation.state, outcome.prompt.who) : outcome.prompt.who;
      return { reason: `waiting on ${who}: "${outcome.prompt.prompt}" — resolve it, then resume`, resumable: true };
    }
    case "rejected":
      // A timeout rejection (the bounded `BrowserMatchHandle` wait) is a transient
      // "wasn't observably settled" signal — safe, and meant, to resume. Any other rejection is
      // the genuine rules-bug case `autoplayActivation`'s own doc says must never be silently
      // retried — surfaced plainly, dismiss-only.
      return outcome.rejection.category === "timeout"
        ? { reason: outcome.rejection.reason, resumable: true }
        : { reason: `rejected: ${outcome.rejection.reason}`, resumable: false };
    case "max_steps":
      return { reason: "hit the step safety valve without finishing — check the activation manually", resumable: false };
  }
}

/** Turn one `autoplaySide` outcome into a GM-facing halt reason, or `null` for a clean `"completed"`. */
export function describeSideHalt(outcome: AutoplaySideOutcome): HaltInfo | null {
  switch (outcome.kind) {
    case "completed":
      return null;
    case "not_acting":
      return { reason: "not this side's turn to activate — nothing to play", resumable: false };
    case "halted":
      // `cause` is the SAME `AutoplayOutcome` shape a lone `playActivation` halt would carry —
      // reuse the one classifier rather than a second copy.
      return describeActivationHalt(outcome.cause) ?? { reason: "halted", resumable: false };
    case "max_activations":
      return { reason: "hit the activation-count safety valve — check the side manually", resumable: false };
  }
}

/** What the bot is doing right now, for the panel to render. */
export type AutoplayBotState =
  | { phase: "idle" }
  | { phase: "running"; label: string }
  | { phase: "halted"; label: string; reason: string; resumable: boolean }
  | { phase: "failed"; label: string; reason: string };

export type AutoplayBotControls = {
  state: AutoplayBotState;
  /** True whenever the one bot slot is occupied (running, halted, OR failed) — every "play" button gates on `!busy`. */
  busy: boolean;
  /** Play (or continue) `unitId`'s own activation. No-op while `busy` or with no live handle. */
  playActivation: (unitId: string, label: string) => void;
  /** Play (or continue) a whole side's activations. No-op while `busy` or with no live handle. */
  playSide: (side: Side, label: string) => void;
  /** Re-run whatever just halted, from the CURRENT authoritative state. No-op when nothing halted (or already running). */
  resume: () => void;
  /** Clear a halted/failed banner without resuming. */
  dismiss: () => void;
};

type BotTarget = { mode: "activation"; unitId: string; label: string } | { mode: "side"; side: Side; label: string };

/**
 * The one bot-run slot for this client (`CombatApp.tsx` lifts ONE instance and threads its
 * `AutoplayBotControls` down to every per-unit panel — see this file's header). `handle` is
 * `null` before the encounter controller connects; both play methods are no-ops until it isn't.
 */
export function useAutoplayBot(handle: BrowserMatchHandle | null, compendium: Compendium): AutoplayBotControls {
  const [state, setState] = useState<AutoplayBotState>({ phase: "idle" });
  // Synchronous double-click/concurrent-run guard — `state` only updates on the next render, so a
  // second click landing before that render would slip past a `state`-only check.
  const runningRef = useRef(false);
  const targetRef = useRef<BotTarget | null>(null);

  const runActivation = useCallback(
    (unitId: string, label: string) => {
      if (runningRef.current || !handle) return;
      runningRef.current = true;
      targetRef.current = { mode: "activation", unitId, label };
      setState({ phase: "running", label });
      autoplayActivation({ handle, unitId, compendium, promptPolicy: browserPromptPolicy })
        .then((outcome) => {
          const halt = describeActivationHalt(outcome);
          setState(halt ? { phase: "halted", label, reason: halt.reason, resumable: halt.resumable } : { phase: "idle" });
        })
        .catch((e: unknown) => {
          setState({ phase: "failed", label, reason: errorMessage(e) });
        })
        .finally(() => {
          runningRef.current = false;
        });
    },
    [handle, compendium],
  );

  const runSide = useCallback(
    (side: Side, label: string) => {
      if (runningRef.current || !handle) return;
      runningRef.current = true;
      targetRef.current = { mode: "side", side, label };
      setState({ phase: "running", label });
      autoplaySide({ handle, side, compendium, promptPolicy: browserPromptPolicy })
        .then((outcome) => {
          const halt = describeSideHalt(outcome);
          setState(halt ? { phase: "halted", label, reason: halt.reason, resumable: halt.resumable } : { phase: "idle" });
        })
        .catch((e: unknown) => {
          setState({ phase: "failed", label, reason: errorMessage(e) });
        })
        .finally(() => {
          runningRef.current = false;
        });
    },
    [handle, compendium],
  );

  const resume = useCallback(() => {
    const t = targetRef.current;
    if (!t || runningRef.current) return;
    if (t.mode === "activation") runActivation(t.unitId, t.label);
    else runSide(t.side, t.label);
  }, [runActivation, runSide]);

  const dismiss = useCallback(() => {
    targetRef.current = null;
    setState({ phase: "idle" });
  }, []);

  return { state, busy: state.phase !== "idle", playActivation: runActivation, playSide: runSide, resume, dismiss };
}
